/**
 * Admin design tokens.
 *
 * Mirrors `design/mockups/admin/tokens.css` (light theme block) at byte
 * level. The mockup HTML is the source of truth — the admin must
 * pixel-match it (LOCK 10 + LOCK 11). Dark theme is deferred to a later
 * ticket (LOCK 10 explicitly limits v1 to light).
 *
 * Spacing base is 4 — same value MUI uses for `spacing(1)` after the
 * theme override in `theme.ts`. Mockup CSS uses `--space-1..--space-10`
 * on the same 4-multiple grid: spacing(1)=4, spacing(2)=8, …, spacing(6)=24.
 *
 * Layout sidebar width is 220px on the live mockup (layout.html line 33)
 * even though tokens.css declares `--sidebar-width: 240px`. The mockup
 * inlines a 220 override; we mirror the live value because LOCK 11 says
 * "pixel-match `layout.html`".
 */
export const tokens = {
  brand: {
    primary: '#0f6b3a',
    primaryHover: '#0d5d33',
    onPrimary: '#ffffff',
    subtle: '#e6f4ed',
  },
  surface: {
    base: '#ffffff',
    elevated: '#f7f9f8',
    muted: '#eff2f0',
    canvas: '#f3f5f4',
    sidebar: '#0c140f',
    sidebarHover: '#15211a',
  },
  text: {
    primary: '#0c140f',
    secondary: '#516159',
    tertiary: '#7b8a82',
    disabled: '#9ba8a1',
    onBrand: '#ffffff',
    onSidebar: '#d3dad6',
    onSidebarMuted: '#8a9892',
  },
  border: {
    default: '#e2e7e5',
    strong: '#c8d2cd',
  },
  state: {
    success: '#16a36a',
    successBg: '#e3f3eb',
    warning: '#e0a300',
    warningBg: '#fbf1d6',
    /**
     * Orange "warning-strong" — the middle defect-severity tier (AUT-151).
     * Sits between `warning` (minor) and `danger` (critical) so the
     * 3-level scale reads as a gradient. Values mirror the mockup
     * `design/mockups/inspection-review.html` `.sev-medium` (`#e8730f`)
     * + the `.sev-badge--medium` text/bg (`#c25f0a` on `~18%` tint).
     */
    warningStrong: '#e8730f',
    warningStrongText: '#c25f0a',
    warningStrongBg: '#fbe7d4',
    danger: '#d6334b',
    dangerBg: '#fbe0e4',
    info: '#1e63d6',
    infoBg: '#dfe9fa',
  },
  shadow: {
    card: '0 1px 2px rgba(12, 20, 15, 0.06)',
    raised: '0 4px 16px rgba(12, 20, 15, 0.1)',
    floating: '0 6px 24px rgba(12, 20, 15, 0.14)',
  },
  font: {
    family: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', 'Helvetica Neue', sans-serif",
    mono: "'SF Mono', 'JetBrains Mono', 'IBM Plex Mono', 'Menlo', monospace",
  },
  /** Desktop type scale (px). */
  type: {
    display: 28,
    h1: 22,
    h2: 18,
    h3: 16,
    body: 14,
    bodySm: 13,
    label: 12,
    caption: 11,
  },
  /** Desktop line-heights (px). */
  line: {
    display: 36,
    h1: 28,
    h2: 24,
    h3: 22,
    body: 20,
    bodySm: 18,
    label: 16,
    caption: 14,
  },
  /** Mockup spacing scale (px). MUI theme.spacing(1) === 4 px. */
  spacing: {
    base: 4,
  },
  radius: {
    pill: 999,
    sm: 4,
    button: 8,
    input: 8,
    card: 12,
    modal: 14,
    sheet: 16,
  },
  layout: {
    sidebarWidth: 220,
    headerHeight: 54,
  },
} as const;

export type DesignTokens = typeof tokens;
